<script setup lang="ts">
import {computed} from "vue";
import {BarChart3, Bot, Hand, Pencil, UserRound} from "@lucide/vue";
import {Button} from "@/core/components/ui/button";
import {Switch} from "@/core/components/ui/switch";
import {BirthdayGift, GiftSendHour} from "@/birthday-gift/interfaces";
import {GIFT_PALETTES, giftProfileLabel} from "@/birthday-gift/constants/palettes";

const props = defineProps<{
    gift: BirthdayGift;
    sendHour: GiftSendHour;
    /** True while the automatic send toggle is awaiting the server. */
    toggling?: boolean;
}>();

const emit = defineEmits<{
    (e: 'edit'): void;
    (e: 'statistics'): void;
    (e: 'toggle-auto', enabled: boolean): void;
}>();

const palette = computed(() => GIFT_PALETTES[props.gift.palette]);
const profileLabel = computed(() => giftProfileLabel(props.gift.age_band, props.gift.sex));

const autoLabel = computed(() => props.gift.auto_send
    ? `Envoi auto à ${String(props.sendHour).padStart(2, '0')}:00`
    : 'Envoi manuel');
</script>

<template>
  <div class="flex flex-col overflow-hidden rounded-lg bg-white shadow">
    <div
      class="relative flex h-[74px] items-center justify-center"
      :style="{background: palette.tint}"
    >
      <component
        :is="palette.icon"
        class="h-8 w-8"
        :style="{color: palette.iconColor}"
      />
      <span
        class="absolute right-2 top-2 rounded-full px-2 py-0.5 text-[10px] font-semibold"
        :class="gift.active ? 'bg-secondary text-primary-dark' : 'bg-gray-light text-muted-foreground'"
      >
        {{ gift.active ? 'Activé' : 'En pause' }}
      </span>
    </div>
    <div class="flex flex-1 flex-col gap-2 p-3.5">
      <div class="text-sm font-semibold leading-snug">
        {{ gift.name }}
      </div>
      <div class="text-xs text-muted-foreground">
        Valable {{ gift.validity_days }} jours · {{ gift.redeemed_count }} cadeaux remis
      </div>
      <div class="flex items-center gap-1.5 text-xs text-primary">
        <UserRound class="h-3.5 w-3.5" />{{ profileLabel }}
      </div>
      <div
        class="flex items-center justify-between gap-2 rounded-lg px-2.5 py-1.5"
        :class="gift.auto_send ? 'bg-primary-lightest' : 'bg-gray-light'"
      >
        <span
          class="flex min-w-0 items-center gap-1.5 text-xs font-semibold"
          :class="gift.auto_send ? 'text-primary' : 'text-muted-foreground'"
        >
          <component
            :is="gift.auto_send ? Bot : Hand"
            class="h-4 w-4 shrink-0"
          />{{ autoLabel }}
        </span>
        <Switch
          :model-value="gift.auto_send"
          :disabled="toggling"
          @update:model-value="emit('toggle-auto', $event)"
        />
      </div>
      <div class="mt-auto flex items-center gap-2 pt-1">
        <Button
          variant="ghost"
          size="sm"
          @click="emit('edit')"
        >
          <Pencil class="h-3.5 w-3.5" />Modifier
        </Button>
        <Button
          variant="ghost"
          size="sm"
          @click="emit('statistics')"
        >
          <BarChart3 class="h-3.5 w-3.5" />Statistiques
        </Button>
      </div>
    </div>
  </div>
</template>
